import { Injectable, OnDestroy } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { ConfigService } from '../services/config.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor, OnDestroy {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private refreshChannel: BroadcastChannel | null = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private http: HttpClient,
    private cs: ConfigService
  ) {
    this.initRefreshChannel();
  }

  ngOnDestroy(): void {
    this.refreshChannel?.close();
  }

  /**
   * Sets up a BroadcastChannel so that when one tab successfully refreshes
   * the token, all other tabs pick up the new JWT without making their own
   * refresh call (which would fail because the old refresh token is revoked).
   */
  private initRefreshChannel(): void {
    try {
      this.refreshChannel = new BroadcastChannel('chronodesk-token-refresh');
      this.refreshChannel.onmessage = (event) => {
        if (event.data?.type === 'TOKEN_REFRESHED' && event.data.tokenData) {
          localStorage.setItem('chronolog-token', JSON.stringify(event.data.tokenData));
          this.refreshTokenSubject.next(event.data.tokenData.JwtToken);
          this.isRefreshing = false;
        } else if (event.data?.type === 'REFRESH_FAILED') {
          this.isRefreshing = false;
          this.handleRefreshFailure();
        }
      };
    } catch {
      // BroadcastChannel not supported — single-tab behaviour only
      this.refreshChannel = null;
    }
  }

  /**
   * Notify other tabs that a refresh succeeded or failed.
   */
  private broadcastRefreshResult(tokenData: any | null): void {
    try {
      if (tokenData) {
        this.refreshChannel?.postMessage({ type: 'TOKEN_REFRESHED', tokenData });
      } else {
        this.refreshChannel?.postMessage({ type: 'REFRESH_FAILED' });
      }
    } catch {
      // ignore if channel is closed
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor logic for auth endpoints (login, password reset, etc.)
    if (this.isAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    // Skip interceptor for non-API requests (e.g. local assets like appsettings.json)
    if (!request.url.includes('/api/')) {
      return next.handle(request);
    }

    // Skip interceptor for requests that are not targeting our known API origins
    const isMainApi = this.cs.apiEndPoint && request.url.startsWith(this.cs.apiEndPoint);
    const isFormsApi = this.cs.formsApiEndPoint && request.url.startsWith(this.cs.formsApiEndPoint);
    const isLoginApi = this.cs.loginEndPoint && request.url.startsWith(this.cs.loginEndPoint);
    const isAiCommandApi = this.cs.aiCommandApiEndPoint && request.url.startsWith(this.cs.aiCommandApiEndPoint);
    if (!isMainApi && !isFormsApi && !isLoginApi && !isAiCommandApi && !request.url.includes('/api/')) {
      return next.handle(request);
    }

    const tokenData = this.getTokenData();
    
    // If we have a token, add it to the request
    if (tokenData && tokenData.JwtToken) {
      request = this.addTokenToRequest(request, tokenData.JwtToken);
      
      const expiry = this.getTokenExpiry(tokenData.JwtToken);
      const now = Date.now() / 1000;
      
      // If token expires in less than 300 seconds, proactively refresh
      if (expiry && expiry - now < 300) {
        return this.handleTokenRefresh(request, next);
      }
    } else {
      // No token found - just pass through the request
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // If we get 401 Unauthorized and we have a token, try to refresh
        if (error.status === 401 && tokenData && tokenData.JwtToken && !this.isAuthEndpoint(request.url)) {
          return this.handleTokenRefresh(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          
          if (response && response.Success && response.JwtToken) {
            // Update stored token
            localStorage.setItem('chronolog-token', JSON.stringify(response));
            this.refreshTokenSubject.next(response.JwtToken);

            // Only broadcast if this tab won the rotation (got a new refresh token).
            // Race-losing tabs get a JWT but no refresh token — they should NOT
            // broadcast because their response is incomplete and would overwrite
            // the winning tab's data in other tabs' localStorage.
            if (response.RefreshToken !== null && response.RefreshToken !== undefined) {
              this.broadcastRefreshResult(response);
            }
            
            // Retry the original request with new token
            return next.handle(this.addTokenToRequest(request, response.JwtToken));
          }
          
          // Refresh failed — only logout this tab, don't broadcast to others
          this.handleRefreshFailure();
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          // Only logout on 401 (unauthorized) — transient errors (500, network)
          // should not log the user out. The next request will retry the refresh.
          if (error?.status === 401) {
            this.handleRefreshFailure();
          }
          return throwError(() => error);
        })
      );
    } else {
      // Wait for the refresh to complete (same tab or cross-tab via BroadcastChannel)
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addTokenToRequest(request, token));
        })
      );
    }
  }

  private refreshToken(): Observable<any> {
    const loginUrl = this.cs.loginEndPoint;
    if (!loginUrl) {
      // Config not loaded yet — cannot refresh, treat as failure
      return throwError(() => new Error('Login endpoint not configured'));
    }
    const companyName = this.getCompanyName();
    
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'company-name': companyName
      }),
      withCredentials: true // Important: send cookies with request
    };

    return this.http.post(`${loginUrl}/api/auth/refresh`, {}, options);
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    // Clone the request and add the Authorization header
    // The Forms API and the AI Command API use standard JwtBearer which requires 'Bearer ' prefix
    // The legacy Timesheet_API accepts the raw token without prefix
    const isFormsApi = this.cs.formsApiEndPoint && request.url.startsWith(this.cs.formsApiEndPoint);
    const isAiCommandApi = this.cs.aiCommandApiEndPoint && request.url.startsWith(this.cs.aiCommandApiEndPoint);
    const usesBearerScheme = isFormsApi || isAiCommandApi;
    const authValue = usesBearerScheme ? `Bearer ${token}` : token;

    const cloneOptions: any = {
      setHeaders: {
        Authorization: authValue
      }
    };
    
    // Preserve withCredentials if it was already set on the original request
    if (request.withCredentials) {
      cloneOptions.withCredentials = true;
    }
    
    return request.clone(cloneOptions);
  }

  private handleRefreshFailure(): void {
    this.auth.logout();
    this.router.navigate(['/account/login']);
  }

  private getTokenData(): any {
    try {
      const tokenStr = localStorage.getItem('chronolog-token');
      return tokenStr ? JSON.parse(tokenStr) : null;
    } catch {
      return null;
    }
  }

  private getTokenExpiry(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp;
    } catch (err) {
      return null;
    }
  }

  private isAuthEndpoint(url: string): boolean {
    const authPaths = [
      '/api/auth/login',
      '/api/auth/refresh',
      '/api/auth/logout',
      '/api/auth/microsoft-login',
      '/api/auth/google-login',
      '/api/auth/xero-login',
      '/api/auth/password-reset',
      '/api/auth/password-update',
      '/api/auth/password-reset-check',
      '/api/auth/GetCompanyInfoByUser',
      '/api/auth/get-microsoft-ids',
      '/api/Auth/Ping'
 
    ];
    
    const lowerUrl = url.toLowerCase();
    
    return authPaths.some(path => {
      const lowerPath = path.toLowerCase();
      // Check if URL contains the path followed by end of string or query params
      return lowerUrl.includes(lowerPath) && 
             (lowerUrl.endsWith(lowerPath) || 
              lowerUrl.includes(lowerPath + '?') ||
              lowerUrl.includes(lowerPath + '/'));
    });
  }

  private getCompanyName(): string {
    const url = window.location.href;
    const urlParts = url.split('/');
    const printMatch = url.match(/print/g);
    const cMatch = url.includes('/c/');
    const positionToExtract = (printMatch && printMatch.length > 1) || cMatch ? 4 : 3;
    
    if (urlParts.length > positionToExtract) {
      return urlParts[positionToExtract] || '';
    }
    return '';
  }
}
